<template>
  <layout>
    <page-header :header="$t('drivers.schedule')" :actions="actions" />
    <b-card id="drivers-schedule" class="mt-2">
      <template #header>
        <DriversScheduleFilters v-model="filters" />
      </template>

      <DriversScheduleTimeline
        ref="timeline"
        :schedule="scheduleData"
        :range="{
          min: filters.date.gte,
          max: filters.date.lte,
        }"
        :show-unassigned="filters.show_unassigned"
        @group-click="onDriverClick"
        @item-add="onBookingAdd"
        @item-move="onBookingMove"
        @item-click="onBookingClick"
      />
    </b-card>

    <BModal
      id="driver-form"
      :hide-header-close="false"
      :no-close-on-backdrop="false"
      :no-close-on-esc="true"
      :hide-footer="true"
    >
      <DriverForm
        v-if="selectedDriver"
        :id="selectedDriver"
        @cancelled="closeDriverModal"
        @close="closeDriverModal"
        @saved="onDriverUpdate"
      />
    </BModal>

    <BookingFormModal
      ref="bookingModal"
      @saved="onBookingSave"
      @deleted="onBookingDelete"
      @booking-cancelled="onBookingDelete"
    />
  </layout>
</template>

<script>
import DriversScheduleFilters from './DriversScheduleFilters.vue'
import DriversScheduleTimeline from './DriversScheduleTimeline.vue'
import BookingsApi from '../bookings/Api'
import { ADMINISTER } from '../constants/permissions'
import { confirmSkipOverlapCheck } from '../bookings/useSlotConflict'

export default {
  name: 'DriversSchedule',

  components: {
    DriversScheduleFilters,
    DriversScheduleTimeline,
    DriverForm: () => import('./Form.vue'),
    BookingFormModal: () => import('../bookings/BookingFormModal.vue'),
  },

  data() {
    return {
      allowMoveToUnassigned: false,
      allowOverlap: false,
      filters: {
        show_unassigned: true,
        company_id: null,
        date: {
          gte: this.$moment().format('YYYY-MM-DD'),
          lte: this.$moment().add(4, 'd').format('YYYY-MM-DD'),
        },
      },
      loading: false,
      scheduleData: null,
      selectedDriver: null,
    }
  },

  computed: {
    actions() {
      let actions = []
      if (this.$allows(ADMINISTER)) {
        actions.push({
          id: 'create',
          text: this.$t('bookings.create'),
          icon: 'icon-plus-circle2',
          onClick: () => {
            this.$refs.bookingModal.show()
          },
        })
      }

      return actions
    },
  },

  watch: {
    'filters.date': {
      deep: true,
      handler(date) {
        if (date.gte && date.lte) {
          this.updateChart()
        }
      },
    },

    'filters.company_id'() {
      this.updateChart()
    },

    currentDivision() {
      this.updateChart()
    },
  },

  methods: {
    fetchSchedule(filters) {
      this.loading = true
      const config = {
        params: {
          where: { ...filters, division_id: this.currentDivision?.id },
        },
      }

      return this.$http
        .get('drivers/schedule', config)
        .then((response) => response.data)
        .finally(() => {
          this.loading = false
        })
    },

    updateChart() {
      const { show_unassigned, ...backendFilters } = this.filters

      this.range = {
        min: this.$moment(this.filters.date.gte),
        max: this.$moment(this.filters.date.lte).endOf('day'),
      }

      const promise = this.fetchSchedule(backendFilters)
        .then((schedule) => {
          this.scheduleData = schedule
        })
        .catch((error) => {
          if (error.message) {
            this.$toast.error(error.message)
          } else {
            this.$toast.error(this.$t('general.error'))
          }
        })

      this.$uiBlocker(promise, this.$el)
    },

    onDriverClick(driverId) {
      this.selectedDriver = driverId
      this.$bvModal.show('driver-form')
    },

    onBookingAdd({ start, driver_id }) {
      if (!this.$allows(ADMINISTER)) {
        return
      }

      this.$refs.bookingModal.show(null, {
        company_id: this.filters.company_id,
        driver_id: driver_id,
        pickup_date: this.$moment(start).format(),
      })
    },

    async onBookingMove({ item, newDriverId, hasOverlap }) {
      if (item.driverId === newDriverId) {
        return
      }

      if (newDriverId === null && !this.allowMoveToUnassigned) {
        this.$alert
          .error(
            this.$t('drivers.messages.new_layout_cannot_move_to_unassigned')
          )
          .then(() => this.$refs.timeline.revertMove(item))
        return
      }

      let skipOverlapCheck = this.allowOverlap
      if (hasOverlap && !skipOverlapCheck) {
        skipOverlapCheck = await confirmSkipOverlapCheck(this.$t.bind(this))

        if (!skipOverlapCheck) {
          this.$refs.timeline.revertMove(item)
          return
        }
      }

      const updateData = {
        driver_id: newDriverId,
        skip_overlap_check: skipOverlapCheck,
      }

      if (
        item.driverId === null &&
        this.filters.company_id &&
        this.$allows(ADMINISTER)
      ) {
        updateData.company_id = this.filters.company_id
      }

      const promise = new BookingsApi()
        .updateCompanyData(item.id, updateData)
        .then(({ message }) => {
          this.$toast.success(message)
        })
        .catch((error) => {
          if (error.message) {
            this.$alert.error(error.message)
          }
          this.$refs.timeline.revertMove(item)
        })

      this.$uiBlocker(promise, this.$refs.timeline.$el)
    },

    onDriverUpdate({ data }) {
      this.$refs.timeline.updateGroup(data)
      this.closeDriverModal()
    },

    closeDriverModal() {
      this.$bvModal.hide('driver-form')
      this.selectedDriver = null
      this.newBooking = null
    },

    onBookingSave({ data }) {
      this.$refs.timeline.addOrUpdateBooking(data)
    },

    onBookingDelete(item) {
      this.$refs.timeline.deleteItem(item)
    },

    onBookingClick(bookingId) {
      this.$refs.bookingModal.show(bookingId)
    },
  },
}
</script>
